import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeColors } from '../../hooks/useThemeColors';
import { useSettings } from '../../context/SettingsContext';
import { useAuth, PERMISSIONS } from '../../context/AuthContext';
import { BiChevronDown, BiX, BiLogOut, BiMoon, BiSun } from 'react-icons/bi';
import { useTheme } from '../../context/ThemeContext';
import { getUploadUrl } from '../../utils/apiBase';
import api from '../../services/api';
import defaultLogo from '../../logo.svg';
import './layout.css';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sidebarColor, sidebarDark, lightenColor, sidebarTextColors } = useThemeColors();
  const { theme, toggleTheme } = useTheme();
  const { companyInfo } = useSettings();
  const { currentUser: authUser, hasPermission, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [counts, setCounts] = useState({ cars: null, clients: null, reservations: null, maintenance: null });
  const [nextService, setNextService] = useState(null);

  const companyName = authUser?.Account?.name || companyInfo?.name || 'Rentora';

  // Sync CSS variables so a per-account custom color still overrides the
  // default lavender/indigo panel from design-system.css. The active-nav
  // pill is always rendered on its own fixed (theme-based, not custom-color)
  // background via --sidebar-active-text, and the user avatar is
  // brand-colored via CSS (var(--color-primary)) — neither depends on this
  // custom color, so they can never go illegible. Everything else that sits
  // directly on the custom-colored panel (brand name, nav text, dividers)
  // gets its foreground recomputed from the chosen color's own luminance,
  // so a dark pick swaps in light text instead of staying dark-on-dark.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--sidebar-bg', sidebarColor);
    root.setProperty('--sidebar-bg-lighter', lightenColor(sidebarColor, 10));
    root.setProperty('--sidebar-bg-darker', sidebarDark);
    root.setProperty('--sidebar-width', '244px');
    root.setProperty('--sidebar-text', sidebarTextColors.text);
    root.setProperty('--sidebar-text-muted', sidebarTextColors.textMuted);
    root.setProperty('--sidebar-active', sidebarTextColors.active);
    root.setProperty('--sidebar-hover', sidebarTextColors.hover);
    root.setProperty('--sidebar-border', sidebarTextColors.border);
  }, [sidebarColor, sidebarDark, lightenColor, sidebarTextColors]);

  // Nav counts + the single most urgent upcoming service, fetched once —
  // a light read of data the rest of the app already loads elsewhere,
  // deduped by the api client if a page fetches it at the same time.
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;

    Promise.allSettled([
      hasPermission(PERMISSIONS.VIEW_CARS) ? api.get('/cars') : Promise.resolve(null),
      hasPermission(PERMISSIONS.VIEW_CLIENTS) ? api.get('/clients') : Promise.resolve(null),
      hasPermission(PERMISSIONS.VIEW_RESERVATIONS) ? api.get('/reservations') : Promise.resolve(null),
      hasPermission(PERMISSIONS.VIEW_MAINTENANCE) ? api.get('/maintenance/maintenance-reminders') : Promise.resolve(null),
    ]).then(([carsRes, clientsRes, reservationsRes, remindersRes]) => {
      if (cancelled) return;
      const cars = carsRes.status === 'fulfilled' ? carsRes.value?.data?.data || [] : [];
      const clients = clientsRes.status === 'fulfilled' ? clientsRes.value?.data?.data || [] : [];
      const reservations = reservationsRes.status === 'fulfilled' ? reservationsRes.value?.data?.data || [] : [];
      const reminders = remindersRes.status === 'fulfilled' ? remindersRes.value?.data?.data || [] : [];

      setCounts({
        cars: cars.length || null,
        clients: clients.length || null,
        reservations: reservations.length || null,
        maintenance: cars.filter(c => ['entretien', 'maintenance'].includes((c.status || '').toLowerCase())).length || null,
      });

      // Most urgent upcoming service: soonest date-based reminder that
      // hasn't fired yet (reminders come with brand/model/plate joined in).
      const now = new Date();
      const dateReminders = reminders
        .filter(r => !r.is_completed && r.target_date)
        .map(r => ({ ...r, _due: new Date(r.target_date) }))
        .filter(r => !isNaN(r._due.getTime()) && r._due >= now)
        .sort((a, b) => a._due - b._due);

      const soonest = dateReminders[0];
      if (soonest) {
        const carLabel = `${soonest.brand || ''} ${soonest.model || ''}`.trim() || t('sidebarAVehicle', 'A vehicle');
        const plate = soonest.plate || '';
        const days = Math.max(0, Math.round((soonest._due - now) / 86400000));
        setNextService({
          text: `${carLabel}${plate ? ' · ' + plate : ''} — ${days <= 0 ? t('sidebarDueToday', 'due today') : t('sidebarDueInDays', { count: days, defaultValue: `due in ${days} day${days === 1 ? '' : 's'}` })}`,
        });
      } else {
        setNextService(null);
      }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // Close on outside click (mobile)
  useEffect(() => {
    const handleOutside = (e) => {
      if (!isOpen || window.innerWidth >= 992) return;
      const sidebar = document.querySelector('.sb');
      if (sidebar && !sidebar.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen, setIsOpen]);

  // Close on route change (mobile)
  useEffect(() => {
    if (window.innerWidth < 992) setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  if (location.pathname === '/login' || !authUser) return null;

  const isActive = (path) => location.pathname === path;

  // Grouped so the sidebar reads as two clear sections instead of one long
  // undifferentiated list: day-to-day fleet operations, then account-level
  // administration (visible only to whoever holds those permissions).
  const navItems = [
    { path: '/dashboard',  label: t('sidebarDashboard'),   perms: [PERMISSIONS.VIEW_DASHBOARD] },
    { path: '/voitures',   label: t('sidebarCars'),        perms: [PERMISSIONS.VIEW_CARS], count: counts.cars },
    { path: '/clients',    label: t('sidebarClients'),     perms: [PERMISSIONS.VIEW_CLIENTS], count: counts.clients },
    { path: '/contracts',  label: t('sidebarContracts'),   perms: [PERMISSIONS.VIEW_RESERVATIONS], count: counts.reservations },
    { path: '/entretiens', label: t('sidebarMaintenance'), perms: [PERMISSIONS.VIEW_MAINTENANCE], count: counts.maintenance },
    { path: '/financial-reports', label: t('sidebarFinancialReports', 'Financial Reports'), perms: [PERMISSIONS.VIEW_FINANCIALS] },
    { path: '/planning',   label: t('sidebarCalendar'),    perms: [PERMISSIONS.VIEW_RESERVATIONS] },
    { path: '/accounts',   label: t('sidebarAccounts'),    perms: [PERMISSIONS.MANAGE_ACCOUNTS], section: 'org' },
    { path: '/users',      label: t('sidebarUsers'),       perms: [PERMISSIONS.MANAGE_ACCOUNT_USERS], section: 'org' },
    { path: '/feedback',   label: t('sidebarFeedback'),    perms: [], masterOnly: true, section: 'org' },
  ].filter(item => {
    if (item.masterOnly && !authUser?.isMasterAdmin) return false;
    if (!item.perms.length) return true;
    return item.perms.every(p => hasPermission(p));
  });
  const mainNavItems = navItems.filter(item => item.section !== 'org');
  const orgNavItems = navItems.filter(item => item.section === 'org');

  const userMenuItems = [
    { path: '/profile',  label: t('sidebarProfile'),   perms: [] },
    { path: '/settings', label: t('settings'),         perms: [PERMISSIONS.VIEW_SETTINGS] },
  ].filter(item => !item.perms.length || item.perms.some(p => hasPermission(p)));

  const roleLabel = {
    MASTER_ADMIN: t('sidebarSuperAdmin'),
    ACCOUNT_ADMIN: t('sidebarAccountAdmin'),
    USER: t('userRole'),
  };
  const displayName = authUser?.firstName && authUser?.lastName
    ? `${authUser.firstName} ${authUser.lastName}`
    : authUser?.username || t('userRole');
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/login');
  };

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`sb__nav-item ${active ? 'active' : ''}`}
      >
        <span>{item.label}</span>
        {item.count !== undefined && item.count !== null && (
          <span className="sb__nav-count">{item.count}</span>
        )}
      </Link>
    );
  };

  return (
    <>
      {isOpen && <div className="sb__overlay d-lg-none" onClick={() => setIsOpen(false)} />}

      <div
        className={`sb ${isOpen ? 'sb--open' : ''}`}
        dir="ltr"
        style={{ background: sidebarColor }}
      >
        {/* Brand */}
        <div className="sb__brand">
          <button className="sb__close-btn d-lg-none" onClick={() => setIsOpen(false)} aria-label="Fermer">
            <BiX />
          </button>
          <div className="sb__logo-box">
            <img
              // The wide marketing banner (/logo.png) was never meant for a
              // small square slot — it's a 16:9 hero graphic with its own
              // dark background baked in, which is exactly what read as a
              // mismatched box. The clean, transparent-background brand mark
              // (already used on the login page) is the right fit here; a
              // real per-account uploaded logo still takes priority and is
              // fetched from the server as before.
              src={companyInfo?.logo_path ? getUploadUrl(companyInfo.logo_path) : defaultLogo}
              alt={companyName}
              className="sb__logo"
              onError={e => { e.target.src = defaultLogo; }}
            />
          </div>
          <span className="sb__brand-name">{companyName}</span>
          <div className="sb__brand-badge">
            <span className="sb__brand-badge-dot" />
            {roleLabel[authUser?.role] || 'Utilisateur'}
          </div>
        </div>

        {/* Navigation — flat, icon-less; hierarchy comes from the active
            item's solid pill (see .sb__nav-item.active in layout.css).
            Grouped into day-to-day operations vs. account administration,
            instead of one long undifferentiated list. */}
        <nav className="sb__nav">
          {mainNavItems.map(item => renderNavItem(item))}

          {orgNavItems.length > 0 && (
            <>
              <div className="sb__nav-section">{t('sidebarOrganization', 'Organization')}</div>
              {orgNavItems.map(item => renderNavItem(item))}
            </>
          )}
        </nav>

        {/* Bottom group: pinned to the bottom via margin-top:auto, so it
            never competes with .sb__nav for grow space. */}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0 }}>
          {/* Next service due */}
          {nextService && hasPermission(PERMISSIONS.VIEW_MAINTENANCE) && (
            <div className="sb__widget">
              <div className="sb__widget-title">{t('sidebarNextService', 'Next service due')}</div>
              <div className="sb__widget-copy">{nextService.text}</div>
              <button className="sb__widget-btn" onClick={() => navigate('/entretiens')}>
                {t('sidebarScheduleIt', 'Schedule it')}
              </button>
            </div>
          )}

          {/* Dark mode toggle */}
          <button className="sb__theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? <BiSun className="sb__theme-icon" /> : <BiMoon />}
            <span>{theme === 'dark' ? t('hdrLightMode') : t('hdrDarkMode')}</span>
          </button>

          {/* User section */}
          <div className="sb__user">
            {userMenuOpen && (
              <div className="sb__dropdown-menu">
                {userMenuItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="sb__dropdown-item"
                    onClick={() => { setUserMenuOpen(false); setIsOpen(false); }}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="sb__dropdown-divider" />
                <button className="sb__dropdown-item sb__dropdown-item--danger" onClick={handleLogout}>
                  <BiLogOut className="sb__dropdown-icon" />
                  {t('sidebarLogout')}
                </button>
              </div>
            )}

            <button
              className="sb__user-toggle"
              onClick={() => setUserMenuOpen(o => !o)}
            >
              <div className="sb__user-avatar" style={authUser?.avatar ? { padding: 0, overflow: 'hidden' } : undefined}>
                {authUser?.avatar
                  ? <img src={authUser.avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : initials}
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div className="sb__user-name">{displayName}</div>
                <div className="sb__user-role">{roleLabel[authUser?.role] || authUser?.role || t('userRole')}</div>
              </div>
              <BiChevronDown className={`sb__chevron ${userMenuOpen ? 'sb__chevron--open' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
